import { createContext, useState, useEffect, useCallback } from 'react';
import {
  signInWithEmail,
  getGoogleSignInUrl,
  getSession,
  signOutSession,
} from '@ego/api';
import { getStoredToken, storeToken, clearStoredToken } from '../lib/api';
import { pullThemesFromServer } from '../utils/themeSync';

export const AuthContext = createContext(null);

export default function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  const persistSession = useCallback((next) => {
    if (next?.access_token) storeToken(next.access_token);
    setSession(next);
  }, []);

  // Resolve the current session via the same-site cookie (used right after an
  // OAuth redirect, when no bearer token is stored yet).
  const refreshSession = useCallback(async () => {
    const next = await getSession(getStoredToken());
    if (next) {
      persistSession(next);
    } else {
      clearStoredToken();
      setSession(null);
    }
    return next;
  }, [persistSession]);

  useEffect(() => {
    // Surface OAuth errors returned on the redirect URL (e.g. signup rejected)
    const params = new URLSearchParams(window.location.search);
    const oauthError = params.get('error');
    if (oauthError) {
      setAuthError(
        /signup/i.test(oauthError)
          ? 'This site is invite-only. Contact the admin for access.'
          : oauthError.replace(/_/g, ' '),
      );
      params.delete('error');
      const query = params.toString();
      window.history.replaceState(null, '', window.location.pathname + (query ? `?${query}` : ''));
    }

    let cancelled = false;
    getSession(getStoredToken())
      .then((next) => {
        if (cancelled) return;
        if (next) {
          storeToken(next.access_token);
          setSession(next);
        } else {
          clearStoredToken();
          setSession(null);
        }
      })
      .catch(() => {
        if (!cancelled) setSession(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  // Cross-device theme sync: pull once whenever a signed-in session becomes
  // available (app load with a stored token, sign-in, OAuth confirm).
  const userId = session?.user?.id;
  useEffect(() => {
    if (userId) pullThemesFromServer();
  }, [userId]);

  const signIn = async (email, password) => {
    try {
      const next = await signInWithEmail(email, password);
      persistSession(next);
      return { error: null };
    } catch (err) {
      return { error: err };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const url = await getGoogleSignInUrl(`${window.location.origin}/auth/confirm`);
      window.location.assign(url);
    } catch (err) {
      setAuthError(err.message || 'Failed to start Google sign-in');
    }
  };

  const signOut = async () => {
    const token = getStoredToken();
    clearStoredToken();
    setSession(null);
    try {
      await signOutSession(token);
    } catch {
      // Local session is already cleared
    }
  };

  if (loading) return <div style={{ minHeight: '100vh', background: 'var(--bg-base)' }} />;

  return (
    <AuthContext.Provider value={{ session, authError, clearAuthError: () => setAuthError(null), signIn, signInWithGoogle, signOut, refreshSession }}>
      {children}
    </AuthContext.Provider>
  );
}
